import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';

import CallexLoader from '../../../components/CallexLoader';
import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';

import { threeSixtyReportDetailsGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import { agentThreeSixtyReportDetailsGet } from '../../../store/slices/callHistorySlice';
import { useUserDetails } from '../../../store/slices/authSlice';
import {
  convertToMask,
  fileDownload,
  formatCellValue,
  MASKED_NUMBER_FIELDS
} from '../../../utils/common';
import config from '../../../config';

// One call has far too many fields for a single horizontal row, so they are laid out
// as label/value pairs, this many pairs across.
const PAIRS_PER_ROW = 2;

const toRows = (fields) => {
  const rows = [];
  for (let i = 0; i < fields.length; i += PAIRS_PER_ROW) {
    rows.push(fields.slice(i, i + PAIRS_PER_ROW));
  }
  return rows;
};

// isAgent switches to the agent endpoint and to the agent screens' conventions
// (translated labels, masking driven by the process setting).
const ThreeSixtyCallDetailsModal = ({ isOpen, handleCancel, uuid, isAgent = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useUserDetails();
  const [isLoading, setIsLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [headerFields, setHeaderFields] = useState({ default: [], crm_fields: [] });
  const [audioDetails, setAudioDetails] = useState(null);

  // The admin side masks like Dump Report (global flag, crm_number only).
  const maskedFields = isAgent
    ? user?.process_details?.numberMasking === 'true'
      ? MASKED_NUMBER_FIELDS
      : []
    : process.env.REACT_APP_GLOBAL_NUMBER_MASKING === 'true'
      ? ['crm_number']
      : [];

  const fetchDetails = async () => {
    setIsLoading(true);
    try {
      const detailsGet = isAgent ? agentThreeSixtyReportDetailsGet : threeSixtyReportDetailsGet;
      const response = await dispatch(detailsGet({ uuid })).unwrap();
      const data = response?.data;
      setDetails((Array.isArray(data) ? data[0] : data) || null);
      setHeaderFields({
        default: response?.header_fields?.default || [],
        crm_fields: response?.header_fields?.crm_fields || []
      });
    } catch (error) {
      setDetails(null);
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && uuid) {
      fetchDetails();
    }
  }, [isOpen, uuid]);

  // Same special cases the Dump Report grid applies to the recording and number fields.
  const renderValue = (field) => {
    const value = details?.[field?.name];
    if (field?.name === 'recording_file') {
      if (!value) return '-';
      const src = `${config.BASE_URL}cdrs_report/download?filename=${value}`;
      return (
        <div className="d-flex gap-2">
          <Icon.PlayCircle
            size={16}
            className="cursor-pointer text-info"
            onClick={() => setAudioDetails({ ...details, src })}
          />
          <Icon.Download size={16} className="cursor-pointer" onClick={() => fileDownload(src)} />
        </div>
      );
    }
    if (maskedFields.includes(field?.name)) {
      return convertToMask(value) || '-';
    }
    return formatCellValue(value);
  };

  const renderSection = (title, fields) => (
    <table className="report-list-table mapping-table flex-shrink-0">
      <colgroup>
        {Array.from({ length: PAIRS_PER_ROW }).map((_, index) => (
          <React.Fragment key={index}>
            <col style={{ width: '18%' }} />
            <col style={{ width: '32%' }} />
          </React.Fragment>
        ))}
      </colgroup>
      <thead>
        <tr>
          <th colSpan={PAIRS_PER_ROW * 2}>{title}</th>
        </tr>
      </thead>
      <tbody>
        {toRows(fields).map((row, index) => (
          <tr key={index}>
            {row.map((field, idx) => (
              <React.Fragment key={idx}>
                <td className="fw-bold bg-light">{field?.label}</td>
                <td>{renderValue(field)}</td>
              </React.Fragment>
            ))}
            {row.length < PAIRS_PER_ROW && <td colSpan={(PAIRS_PER_ROW - row.length) * 2} />}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <Modal isOpen={isOpen} size="xl" centered toggle={handleCancel}>
        <ModalHeader toggle={handleCancel}>
          {isAgent ? t('callDetails') : 'Call Details'}
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '200px' }}>
              <CallexLoader />
            </div>
          ) : details ? (
            <div
              className="daily-report-table flex-column"
              style={{ maxHeight: 'calc(100vh - 190px)' }}>
              {renderSection(isAgent ? t('default') : 'Default', headerFields.default)}
              {headerFields.crm_fields.length > 0 &&
                renderSection(isAgent ? t('crm') : 'CRM', headerFields.crm_fields)}
            </div>
          ) : (
            <div
              className="d-flex justify-content-center align-items-center no-data"
              style={{ height: '200px' }}>
              {isAgent ? t('noDataAvailable') : 'No details available'}
            </div>
          )}
        </ModalBody>
      </Modal>
      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
        />
      )}
    </>
  );
};

ThreeSixtyCallDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func,
  uuid: PropTypes.string,
  isAgent: PropTypes.bool
};

export default ThreeSixtyCallDetailsModal;
